import { useState, useCallback, useMemo, useRef } from 'react';
import type { Message, ChatResponse } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

// Button-Wert, den der Chat selbst abfängt: schickt die letzte gescheiterte Anfrage
// noch einmal ab und geht nie ans Backend.
export const RETRY_BUTTON_VALUE = '__erneut_versuchen__';

// Header für alle Folgeaufrufe einer Sitzung. Ohne Token (alte Sitzungen, altes
// Backend) bleibt der Header einfach weg, das Backend lässt solche Aufrufe durch.
export function sessionHeaders(sessionToken: string | null): Record<string, string> {
  return sessionToken ? { 'X-Session-Token': sessionToken } : {};
}

class ChatApiError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.status = status;
  }
}

function getTrackingData() {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    utmSource: urlParams.get('utm_source') || undefined,
    utmMedium: urlParams.get('utm_medium') || undefined,
    utmCampaign: urlParams.get('utm_campaign') || undefined,
    utmTerm: urlParams.get('utm_term') || undefined,
    utmContent: urlParams.get('utm_content') || undefined,
  };
}

// Fehlerantworten des Backends (400/403/404/429/500) sind gültiges JSON ohne
// message-Feld. Ohne diese Prüfung landete der Fehler als leere Sprechblase im
// Chat statt im catch-Zweig.
async function readChatResponse(response: Response): Promise<ChatResponse> {
  if (!response.ok) {
    throw new ChatApiError(response.status);
  }

  const data: Partial<ChatResponse> = await response.json();

  if (typeof data.message !== 'string' || !data.message.trim()) {
    throw new Error('Leere Antwort vom Server');
  }

  return data as ChatResponse;
}

function fehlerNachricht(error: unknown): Pick<Message, 'content' | 'buttons'> {
  const retryButton = { label: 'Erneut versuchen', value: RETRY_BUTTON_VALUE, primary: true };

  if (error instanceof ChatApiError && error.status === 403) {
    return {
      content: 'Diese Sitzung ist nicht mehr gültig. Bitte starten Sie eine neue Prüfung.',
      buttons: [{ label: 'Neue Prüfung starten', value: 'neu', primary: true }],
    };
  }

  if (error instanceof ChatApiError && error.status === 429) {
    return {
      content: 'Es wurden gerade zu viele Anfragen gesendet. Bitte warten Sie einen Moment und versuchen Sie es dann erneut.',
      buttons: [retryButton],
    };
  }

  // Sitzung nicht gefunden: Wiederholen hilft nur bei einem kurzen Datenbank-Aussetzer,
  // deshalb zusätzlich den Neustart anbieten
  if (error instanceof ChatApiError && error.status === 404) {
    return {
      content: 'Ihre Sitzung wurde nicht gefunden. Sie können es noch einmal versuchen oder eine neue Prüfung starten.',
      buttons: [retryButton, { label: 'Neue Prüfung starten', value: 'neu' }],
    };
  }

  return {
    content: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
    buttons: [retryButton],
  };
}

export function useChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Bewusst nur im Speicher und nicht in localStorage: das Token schützt die Sitzung
  // und soll den Tab nicht überleben.
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [currentInputType, setCurrentInputType] = useState<string>('none');
  const [currentPlaceholder, setCurrentPlaceholder] = useState<string>('');
  const [currentButtons, setCurrentButtons] = useState<Message['buttons']>([]);
  // Letzte Anfrage für „Erneut versuchen" sowie die Fehlerblase, die dabei verschwindet
  const lastRequestRef = useRef<{ apiMessage: string; metadata?: Record<string, unknown>; clientRequestId: string } | null>(null);
  const lastErrorIdRef = useRef<string | null>(null);

  const isTesterMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tester') === 'true';
  }, []);

  const startChat = useCallback(async () => {
    setIsLoading(true);
    setStartError(null);
    try {
      const response = await fetch(`${API_URL}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(getTrackingData()),
      });

      const data = await readChatResponse(response);

      if (!data.sessionId) {
        throw new Error('Antwort ohne Sitzungs-ID');
      }

      setSessionId(data.sessionId);
      if (data.sessionToken) {
        setSessionToken(data.sessionToken);
      }
      setMessages([{
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        buttons: data.buttons,
        inputType: data.inputType,
        inputPlaceholder: data.inputPlaceholder,
      }]);
      setCurrentButtons(data.buttons);
      setCurrentInputType(data.inputType || 'none');
      setCurrentPlaceholder(data.inputPlaceholder || '');
    } catch (error) {
      console.error('Failed to start chat:', error);
      setStartError(
        error instanceof ChatApiError && error.status === 429
          ? 'Es wurden gerade zu viele Anfragen gesendet. Bitte warten Sie einen Moment und versuchen Sie es dann erneut.'
          : 'Die Prüfung konnte gerade nicht gestartet werden. Bitte prüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const performRequest = useCallback(async (
    apiMessage: string,
    metadata?: Record<string, unknown>,
    // Beim Wiederholen dieselbe Kennung: Hat das Backend die Anfrage schon
    // verarbeitet und nur die Antwort ging verloren, liefert es die gespeicherte
    // Antwort, statt die Nachricht ein zweites Mal auf die nächste Frage zu buchen.
    clientRequestId: string = crypto.randomUUID()
  ) => {
    lastRequestRef.current = { apiMessage, metadata, clientRequestId };
    setIsLoading(true);
    setCurrentButtons([]);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders(sessionToken) },
        body: JSON.stringify({
          sessionId,
          message: apiMessage,
          metadata,
          clientRequestId,
          ...getTrackingData(),
        }),
      });

      const data = await readChatResponse(response);

      if (data.sessionId) {
        setSessionId(data.sessionId);
      }
      if (data.sessionToken) {
        setSessionToken(data.sessionToken);
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        buttons: data.buttons,
        inputType: data.inputType,
        inputPlaceholder: data.inputPlaceholder,
      };

      setMessages(prev => [...prev, assistantMessage]);
      setCurrentButtons(data.buttons);
      setCurrentInputType(data.inputType || 'none');
      setCurrentPlaceholder(data.inputPlaceholder || '');
    } catch (error) {
      console.error('Failed to send message:', error);
      const fehler = fehlerNachricht(error);
      const errorId = crypto.randomUUID();
      lastErrorIdRef.current = errorId;
      setMessages(prev => [...prev, { id: errorId, role: 'assistant', ...fehler }]);
      setCurrentButtons(fehler.buttons);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, sessionToken]);

  const sendMessageInternal = useCallback((
    apiMessage: string,
    displayMessage: string,
    metadata?: Record<string, unknown>
  ) => {
    if (!displayMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: displayMessage,
    };

    setMessages(prev => [...prev, userMessage]);
    void performRequest(apiMessage, metadata);
  }, [performRequest, isLoading]);

  const sendMessage = useCallback((content: string, metadata?: Record<string, unknown>) => {
    sendMessageInternal(content, content, metadata);
  }, [sendMessageInternal]);

  const sendButtonClick = useCallback((value: string, label: string) => {
    sendMessageInternal(value, label);
  }, [sendMessageInternal]);

  // Schickt die letzte gescheiterte Anfrage erneut, ohne die Nutzer-Nachricht zu
  // doppeln. Die Fehlerblase verschwindet, damit die Antwort direkt darunter steht.
  const retryLastRequest = useCallback(() => {
    const last = lastRequestRef.current;
    if (!last || isLoading) return;

    const errorId = lastErrorIdRef.current;
    if (errorId) {
      setMessages(prev => prev.filter(m => m.id !== errorId));
      lastErrorIdRef.current = null;
    }

    void performRequest(last.apiMessage, last.metadata, last.clientRequestId);
  }, [performRequest, isLoading]);

  return {
    messages,
    sessionId,
    sessionToken,
    isLoading,
    startError,
    isTesterMode,
    currentInputType,
    currentPlaceholder,
    currentButtons,
    startChat,
    sendMessage,
    sendButtonClick,
    retryLastRequest,
  };
}
